import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Box, Dialog, DialogContent, LinearProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import { shuffle } from 'lodash';
import { GridSortModel, type GridSortItem } from '@mui/x-data-grid-pro';

import {
  type Provider,
  TransportationProviderTierBC,
} from '../../../../data/models/provider.model';
import type {
  SyncedTripLeg,
  TripLeg,
} from '../../../../data/models/reservation.model';
import type { SortModel } from '../../../../data/models/sort-model.model';
import { ProviderCallOutcomeBC } from '../../../../data/models/provider-call-outcome.model';
import { ProviderServicePricing } from '../../../../data/models/provider-service-pricing.model';
import { TripLegStatus } from '../../../../data/models/trip-leg-status.model';

import {
  fetchCoverageAreasAsync,
  fetchProviderListAsync,
  fetchProvidersCallOutcomesAsync,
  saveProviderCallOutcomeAsync,
  selectAcceptedProviderCallOutcome,
  selectCoverageAreas,
  selectLoadedProviderCallOutcomes,
  selectLoadedProviderList,
  selectLoadedProviderListError,
  selectLoadingProviderCallOutcomes,
  selectLoadingProviderList,
  selectProviderCallOutcomes,
  selectProviderList,
  selectSavedProviderCallOutcome,
  selectSavingProviderCallOutcome,
  setAcceptedProviderCallOutcome,
  setLoadedProviderCallOutcomes,
  setLoadedProviderList,
  setLoadedProviderListError,
  setLoadingProviderCallOutcomes,
  setLoadingProviderList,
  setSavedProviderCallOutcome,
} from '../../../../data/provider.slice';
import {
  fetchClientServicePricingsAsync,
  fetchEstimatedTripLegPricingAsync,
  fetchProviderServicePricingsAsync,
  fetchTransportationServicesAsync,
  selectAllProviderServicePricings,
  selectLoadingClientServicePricings,
  selectLoadingProviderServicePricings,
} from '../../../../data/accounting.slice';
import {
  fetchLookupItemsAsync,
  selectCallOutcomeRejectReasons,
  selectCallOutcomes,
} from '../../../../data/lookup.slice';
import {
  fetchAllTripLegsInReservationAsync,
  selectAllTripLegsInReservation,
  escalateTripLegAsync,
  selectEscalatedTripLeg,
  setEscalatedTripLeg,
  selectLoadedProvidersMatchingTripLeg,
  selectLoadingProvidersMatchingTripLeg,
  selectProvidersMatchingTripLeg,
  fetchProvidersMatchingTripLegAsync,
  setLoadedProvidersMatchingTripLeg,
  setLoadingProvidersMatchingTripLeg,
  selectLoadingAllTripLegsInReservation,
  fetchValidTripLegAllocationProperties,
  selectLoadedValidTripLegAllocationProperties,
  selectValidTripLegAllocationProperties,
  setTripLegExceptionAssignmentAsync,
  selectLoadedAllTripLegsInReservation,
  setLoadedAllTripLegsInReservation,
  setLoadingAllTripLegsInReservation,
} from '../../../../data/tripleg.slice';
import * as uiSlice from '../../../../data/ui.slice';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';

import { FullscreenDialogHeader } from '../../../dialogs/FullscreenDialogHeader';
import { DialogAttempts } from '../../../dialogs/DialogAttempts';
import { ProviderCallForm } from './ProviderCallForm';
import { ProviderSelectForm } from './ProviderSelectForm';

import {
  MAX_FIND_PROVIDER_ATTEMPTS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE,
} from '../../../../data/constants';
import { useCanReadEscalation } from '../../../../hooks/permissions/useCanReadEscalation';
import triplegUtils from '../../../../services/triplegUtils';
import { selectLoggedInUserInfo } from '../../../../data/user.slice';

const PREFIX = 'FindProviderDialog';

const classes = {
  content: `${PREFIX}-content`,
  pages: `${PREFIX}-pages`,
};

const Root = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    paddingLeft: 0,
    paddingRight: 0,
  },
  [`& .${classes.pages}`]: {
    display: 'flex',
    gap: theme.spacing(3),
    paddingLeft: theme.spacing(18),
  },
}));

interface FindProviderDialogProps {
  tripLeg: SyncedTripLeg;
  open: boolean;
  close: () => void;
}

function FindProviderDialog({ tripLeg, open, close }: FindProviderDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const isTripLegEscalated = tripLeg.isEscalated ?? false;
  const canReadEscalation = useCanReadEscalation();

  const [page, setPage] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState<number | null>(null);
  const selectedFilter = 'ctt0&active0'; // only get cleared and active providers
  const [searchFilter, setSearchFilter] = useState<string | null>(null);
  const defaultSortItem: GridSortItem = {
    field: 'transportationProviderName',
    sort: 'asc',
  };
  const [sortModel, setSortModel] = useState<GridSortModel>([defaultSortItem]);
  const sortItem = sortModel.length > 0 ? sortModel[0] : defaultSortItem;
  const mappedSortModel: SortModel = {
    column: sortItem.field,
    direction: sortItem.sort,
  };
  const [filteredProvidersList, setFilteredProvidersList] = useState<
    Provider[]
  >([]);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null,
  );
  const [previouslySelectedProvidersIdxs, setPreviouslySelectedProvidersIdxs] =
    useState<number[]>([]);

  const [providerServicePricing, setProviderServicePricing] =
    useState<ProviderServicePricing | null>(null);

  const [transportationProviderIDs, setTransportationProviderIDs] = useState<
    number[] | null
  >(null);

  const [coverageAreaID, setCoverageAreaID] = useState<number | null>(null);
  const [clientTransportationServiceUUID, setClientTransportationServiceUUID] =
    useState<string | null>(null);
  const [
    providerTransportationServiceUUID,
    setProviderTransportationServiceUUID,
  ] = useState<string | null>(null);

  const validTripLegAllocationProperties = useAppSelector(
    selectValidTripLegAllocationProperties,
  );
  const loadedValidTripLegAllocationProperties = useAppSelector(
    selectLoadedValidTripLegAllocationProperties,
  );

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: {
      callOutcomeBC: '',
      // serviceAssurance: '',
      rejectedReason: '',
      tripLegIDs: [{ value: false }],
      rejectedReasonOther: '',
    },
  });

  const callOutcomeBC = formHook.watch('callOutcomeBC');
  // const serviceAssurance = formHook.watch('serviceAssurance');
  const rejectedReasonID = formHook.watch('rejectedReason');
  const rejectedReasonOther = formHook.watch('rejectedReasonOther');
  const tripLegIDs = formHook.watch('tripLegIDs');

  const callOutcomes = useAppSelector(selectCallOutcomes);
  const rejectedReasonOptions = useAppSelector(selectCallOutcomeRejectReasons);
  const loadingProvidersList = useAppSelector(selectLoadingProviderList);
  const loadedProvidersList = useAppSelector(selectLoadedProviderList);
  const loadedProviderListError = useAppSelector(selectLoadedProviderListError);
  const providersList = useAppSelector(selectProviderList);
  const providersMatchingTripLeg = useAppSelector(
    selectProvidersMatchingTripLeg,
  );
  const loadingProvidersMatchingTripLeg = useAppSelector(
    selectLoadingProvidersMatchingTripLeg,
  );
  const loadedProvidersMatchingTripLeg = useAppSelector(
    selectLoadedProvidersMatchingTripLeg,
  );
  const coverageAreas = useAppSelector(selectCoverageAreas);
  const loadingClientServicePricings = useAppSelector(
    selectLoadingClientServicePricings,
  );
  const providerServicePricings = useAppSelector(
    selectAllProviderServicePricings,
  );
  const loadingProviderServicePricings = useAppSelector(
    selectLoadingProviderServicePricings,
  );
  const allTripLegsInReservation = useAppSelector(
    selectAllTripLegsInReservation,
  );
  const filteredTripLegsInReservation = allTripLegsInReservation.filter(
    (tripLegInReservation) =>
      tripLegInReservation.status === TripLegStatus.Open,
  );
  const loadedAllTripLegsInReservation = useAppSelector(
    selectLoadedAllTripLegsInReservation,
  );
  const loadingAllTripLegsInReservation = useAppSelector(
    selectLoadingAllTripLegsInReservation,
  );
  const savingProviderCallOutcome = useAppSelector(
    selectSavingProviderCallOutcome,
  );
  const savedProviderCallOutcome = useAppSelector(
    selectSavedProviderCallOutcome,
  );
  const acceptedProviderCallOutcome = useAppSelector(
    selectAcceptedProviderCallOutcome,
  );
  const escalatedTripLeg = useAppSelector(selectEscalatedTripLeg);
  const loadingProviderCallOutcomes = useAppSelector(
    selectLoadingProviderCallOutcomes,
  );
  const loadedProviderCallOutcomes = useAppSelector(
    selectLoadedProviderCallOutcomes,
  );
  const providerCallOutcomes = useAppSelector(selectProviderCallOutcomes);

  const numberOfPreviousAttempts =
    tripLeg.tripLegProviderCallOutcomes?.length ?? 0;

  const [currentProviderIndex, setCurrentProviderIndex] = useState(
    numberOfPreviousAttempts,
  );
  const [attempt, setAttempt] = useState(numberOfPreviousAttempts + 1);
  const loggedInUserInfo = useAppSelector(selectLoggedInUserInfo);

  const isLoading =
    loadingProvidersList ||
    loadingProvidersMatchingTripLeg ||
    loadingAllTripLegsInReservation ||
    loadingClientServicePricings ||
    loadingProviderServicePricings ||
    loadingProviderCallOutcomes;

  const isAllocationsValid =
    !!clientTransportationServiceUUID &&
    !!providerTransportationServiceUUID &&
    !!coverageAreaID;

  // The system will only ever allow a maximum of 3 attempts, but there may
  // be less than 3 providers, or less than 3 providers matching the trip leg,
  // so we should display the number of attempts that are possible
  const maximumProviderAttempts = Math.min(
    MAX_FIND_PROVIDER_ATTEMPTS,
    providersList.itemCount,
    providersMatchingTripLeg.transportationProviderIDs.length,
  );

  const subTitle = tripLeg.tripLegReferenceID ?? '';
  const title = t('findProvider.findProvider');

  const isAllProvidersAlreadyCalled =
    tripLeg.tripLegProviderCallOutcomes?.length === providersList.itemCount;

  const handleClose = () => {
    formHook.reset();
    dispatch(setLoadingProvidersMatchingTripLeg(false));
    dispatch(setLoadedProvidersMatchingTripLeg(false));
    dispatch(setLoadingProviderCallOutcomes(false));
    dispatch(setLoadedProviderCallOutcomes(false));
    dispatch(setLoadingProviderList(false));
    dispatch(setLoadedProviderList(false));
    dispatch(setLoadingAllTripLegsInReservation(false));
    dispatch(setLoadedAllTripLegsInReservation(false));
    dispatch(setSavedProviderCallOutcome(false));
    dispatch(setAcceptedProviderCallOutcome(false));
    dispatch(setEscalatedTripLeg(false));

    setPage(DEFAULT_PAGE);
    setPageSize(DEFAULT_PAGE_SIZE);
    setSearchFilter(null);

    if (canReadEscalation && selectedProvider) {
      setSelectedProvider(null);
    } else {
      setFilteredProvidersList([]);
      setTransportationProviderIDs(null);
      setCurrentProviderIndex(0);
      setPreviouslySelectedProvidersIdxs([]);
      setAttempt(1);
      close();
    }
  };

  const handleSubmit = () => {
    const isValid = formHook.formState.isValid;
    const isDirty = formHook.formState.isDirty;
    if (
      isValid &&
      isDirty &&
      isAllocationsValid &&
      !savingProviderCallOutcome
    ) {
      const tripLegsToSave = [];
      if (callOutcomeBC === ProviderCallOutcomeBC.Accepted) {
        for (let i = 0; i < filteredTripLegsInReservation.length; i++) {
          if (tripLegIDs[i]?.value) {
            tripLegsToSave.push(
              filteredTripLegsInReservation[i].tripLegReferenceID!,
            );
          }
        }
      } else {
        tripLegsToSave.push(tripLeg.tripLegReferenceID!);
      }
      const thisCallOutcome = callOutcomes.find(
        (outcome) => outcome.callOutcomeBC === callOutcomeBC,
      );
      if (thisCallOutcome) {
        setPreviouslySelectedProvidersIdxs([
          ...previouslySelectedProvidersIdxs,
          currentProviderIndex,
        ]);
        dispatch(
          saveProviderCallOutcomeAsync({
            tripLegReferenceIDs: tripLegsToSave,
            callOutcomeID: thisCallOutcome.callOutcomeID,
            callOutcomeBC: thisCallOutcome.callOutcomeBC,
            callOutcomeRejectReasonID: parseInt(rejectedReasonID, 10),
            rejectedReason: rejectedReasonOther,
            transportationProviderID:
              selectedProvider?.transportationProviderID ?? 0,
            clientTransportationServiceUUID,
            providerTransportationServiceUUID,
            coverageAreaID,
            rejectedReasonBC:
              rejectedReasonOptions.find(
                (reason) =>
                  reason.callOutcomeRejectReasonID ===
                  parseInt(rejectedReasonID, 10),
              )?.callOutcomeRejectReasonBC ?? undefined,
            passengerUUID: tripLeg.passengerUUID!,
            clearException: true,
          }),
        );
      }
    }
  };

  const escalateTripLeg = ({ force = false }: { force?: boolean } = {}) => {
    // Trip Legs should only be escalated if under the following conditions:
    // 1. The trip is not already escalated
    // 2. The user viewing the trip leg doesn't have permission to read escalations
    // 3. The call outcome attempts have exceeded the maximum number of attempts
    if (force) {
      dispatch(escalateTripLegAsync(tripLeg.tripLegReferenceID!));
      return;
    }
    if (
      !loadedProvidersMatchingTripLeg ||
      !loadedProviderCallOutcomes ||
      !loadedAllTripLegsInReservation ||
      isTripLegEscalated ||
      canReadEscalation ||
      (attempt <= maximumProviderAttempts && !canReadEscalation)
    ) {
      return;
    }
    dispatch(escalateTripLegAsync(tripLeg.tripLegReferenceID!));
  };

  const fetchProviderList = () => {
    dispatch(
      fetchProviderListAsync({
        pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
        page: page ?? DEFAULT_PAGE,
        filter: selectedFilter,
        search: searchFilter ?? '',
        sortModel: mappedSortModel,
        transportationProviderIDs: transportationProviderIDs ?? undefined,
      }),
    );
  };

  const buildProvidersList = () => {
    // First add all matching providers, randomized
    const matchingProviders = shuffle(
      providersList.items.filter((provider) => {
        return (
          providersMatchingTripLeg.transportationProviderIDs?.includes(
            Number(provider.transportationProviderID),
          ) ?? false
        );
      }),
    );
    // Then add all remaining providers that are Tier 1, randomized
    const remainingProviders = shuffle(
      providersList.items.filter((provider) => {
        return (
          !providersMatchingTripLeg.transportationProviderIDs?.includes(
            Number(provider.transportationProviderID),
          ) &&
          provider.transportationProviderTierBC ===
            TransportationProviderTierBC.Tier1
        );
      }),
    );
    const filteredProvidersList = [...matchingProviders, ...remainingProviders];
    setFilteredProvidersList(filteredProvidersList);
  };

  useEffect(() => {
    if (savedProviderCallOutcome) {
      formHook.reset();
      if (canReadEscalation) {
        setSelectedProvider(null);
      } else {
        if (
          attempt >= maximumProviderAttempts &&
          callOutcomeBC !== ProviderCallOutcomeBC.Accepted
        ) {
          escalateTripLeg({ force: true });
        } else if (attempt < maximumProviderAttempts) {
          setAttempt(attempt + 1);
          const nextProviderIdx = currentProviderIndex + 1;
          setCurrentProviderIndex(nextProviderIdx);
        }
      }
      dispatch(setSavedProviderCallOutcome(false));
    }
  }, [savedProviderCallOutcome]);

  useEffect(() => {
    const zipCode = (
      tripLeg as unknown as SyncedTripLeg
    ).pickUpZipCode.substring(0, 5);
    const stateCode = (tripLeg as unknown as SyncedTripLeg).pickUpStateCode;
    dispatch(fetchLookupItemsAsync('callOutcome'));
    dispatch(fetchLookupItemsAsync('callOutcomeRejectReason'));
    dispatch(fetchLookupItemsAsync('transportationProviderPhoneUse'));
    dispatch(fetchClientServicePricingsAsync(tripLeg.clientBC!));
    dispatch(fetchCoverageAreasAsync(stateCode, zipCode));
    dispatch(fetchTransportationServicesAsync());
    dispatch(
      fetchAllTripLegsInReservationAsync(
        tripLeg.reservationReferenceID!,
        tripLeg.passengerUUID!,
      ),
    );
    if (!isTripLegEscalated) {
      dispatch(
        fetchProvidersCallOutcomesAsync({
          tripLegReferenceID: tripLeg.tripLegReferenceID!,
          sortModel: mappedSortModel,
          page: page ?? DEFAULT_PAGE,
          pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
          transportationProviderTier: '1,2,3',
          hasCallOutcome: true,
        }),
      );
    }

    // Check if the current assignee is the same as the current user
    // if not, assign ourselves to the trip leg
    const exceptionAssignmentUserID = triplegUtils.getExceptionAssignmentUserID(
      tripLeg as unknown as TripLeg,
    );
    if (exceptionAssignmentUserID !== loggedInUserInfo.userAccountID!) {
      dispatch(
        setTripLegExceptionAssignmentAsync(
          tripLeg.tripLegReferenceID!,
          loggedInUserInfo?.userAccountID!,
        ),
      );
    }
  }, []);

  // Once the matching provider list is loaded, we can fetch the provider list
  // If there are no matching providers, and the user can't read escalations
  // we can escalate the trip leg
  useEffect(() => {
    if (
      loadedProvidersMatchingTripLeg &&
      providersMatchingTripLeg &&
      canReadEscalation
    ) {
      if (providersMatchingTripLeg.transportationProviderIDs.length > 0) {
        setTransportationProviderIDs(
          providersMatchingTripLeg.transportationProviderIDs,
        );
      } else {
        // If a user can read escalations, we display all providers
        setTransportationProviderIDs(null);
      }
    } else if (
      loadedProvidersMatchingTripLeg &&
      providersMatchingTripLeg &&
      !canReadEscalation
    ) {
      if (providersMatchingTripLeg.transportationProviderIDs.length === 0) {
        escalateTripLeg();
      } else {
        setTransportationProviderIDs(
          providersMatchingTripLeg.transportationProviderIDs,
        );
      }
    }
  }, [loadedProvidersMatchingTripLeg]);

  useEffect(() => {
    if (transportationProviderIDs) {
      fetchProviderList();
    }
  }, [transportationProviderIDs]);

  useEffect(() => {
    if (searchFilter !== null) {
      setPage(DEFAULT_PAGE);
      fetchProviderList();
    }
  }, [searchFilter]);

  useEffect(() => {
    if (page !== null) {
      fetchProviderList();
    }
  }, [page]);

  useEffect(() => {
    if (pageSize !== null) {
      fetchProviderList();
    }
  }, [pageSize]);

  useEffect(() => {
    if (acceptedProviderCallOutcome) {
      handleClose();
      dispatch(setAcceptedProviderCallOutcome(false));
    }
  }, [acceptedProviderCallOutcome]);

  useEffect(() => {
    if (escalatedTripLeg) {
      handleClose();
      dispatch(setEscalatedTripLeg(false));
    }
  }, [escalatedTripLeg]);

  useEffect(() => {
    if (
      filteredProvidersList.length > 0 &&
      attempt <= maximumProviderAttempts &&
      !savingProviderCallOutcome
    ) {
      setSelectedProvider(filteredProvidersList[currentProviderIndex]);
    }
  }, [currentProviderIndex, filteredProvidersList]);

  // Once the matching providers is loaded from F1 and Transport, then we can
  // build the list of providers to display
  useEffect(() => {
    if (loadedProvidersList) {
      if (!canReadEscalation && previouslySelectedProvidersIdxs.length === 0) {
        buildProvidersList();
        setCurrentProviderIndex(0);
      } else if (canReadEscalation && isAllProvidersAlreadyCalled) {
        if (searchFilter === '' || searchFilter === null) {
          setTransportationProviderIDs(null);
          fetchProviderList();
        }
      }
      dispatch(setLoadedProviderList(false));
    }
  }, [loadedProvidersList]);

  useEffect(() => {
    if (loadedProviderListError) {
      dispatch(setLoadedProviderListError(null));
      handleClose();
    }
  }, [loadedProviderListError]);

  useEffect(() => {
    if (selectedProvider?.transportationProviderID) {
      if (
        clientTransportationServiceUUID &&
        providerTransportationServiceUUID &&
        coverageAreaID
      ) {
        dispatch(
          fetchProviderServicePricingsAsync(
            selectedProvider?.transportationProviderID,
          ),
        );
        for (let i = 0; i < filteredTripLegsInReservation.length; i++) {
          dispatch(
            fetchEstimatedTripLegPricingAsync({
              tripLegReferenceID:
                filteredTripLegsInReservation[i].tripLegReferenceID!,
              transportationProviderID:
                selectedProvider.transportationProviderID,
              passengerUUID: tripLeg.passengerUUID!,
              coverageAreaID,
              providerTransportationServiceUUID,
            }),
          );
        }
      }
    }
  }, [
    clientTransportationServiceUUID,
    providerTransportationServiceUUID,
    coverageAreaID,
  ]);

  useEffect(() => {
    if (loadedValidTripLegAllocationProperties) {
      if (validTripLegAllocationProperties?.coverageAreaID) {
        setCoverageAreaID(validTripLegAllocationProperties.coverageAreaID);
      }
      if (
        validTripLegAllocationProperties?.clientTransportationServiceUUIDs
          ?.length
      ) {
        setClientTransportationServiceUUID(
          validTripLegAllocationProperties.clientTransportationServiceUUIDs[0],
        );
      }
      if (
        validTripLegAllocationProperties?.providerTransportationServiceUUIDs
          ?.length
      ) {
        setProviderTransportationServiceUUID(
          validTripLegAllocationProperties
            .providerTransportationServiceUUIDs[0],
        );
      }
    }
  }, [loadedValidTripLegAllocationProperties]);

  useEffect(() => {
    if (
      selectedProvider?.transportationProviderID &&
      tripLeg.passengerUUID &&
      tripLeg.tripLegReferenceID &&
      !savingProviderCallOutcome &&
      previouslySelectedProvidersIdxs.length === 0
    ) {
      dispatch(
        fetchValidTripLegAllocationProperties({
          passengerUUID: tripLeg.passengerUUID!,
          tripLegReferenceID: tripLeg.tripLegReferenceID!,
          transportationProviderID: selectedProvider.transportationProviderID,
        }),
      );
    }
  }, [
    selectedProvider?.transportationProviderID,
    tripLeg.passengerUUID,
    tripLeg.tripLegReferenceID,
  ]);

  useEffect(() => {
    if (coverageAreas.length > 0 && providerServicePricings) {
      const thisProviderServicePricing = providerServicePricings.filter(
        (providerServicePricing) =>
          providerServicePricing.state === 'active' &&
          providerServicePricing.coverageAreaIDs.includes(
            coverageAreas[0]?.coverageAreaID ?? 0,
          ),
      );
      setProviderServicePricing(thisProviderServicePricing[0]);
    } else {
      // If we can't get any matching services or coverage areas, PR2 still
      // needs to send in a service pricing to manually allocate
      if (canReadEscalation) {
        setProviderServicePricing(providerServicePricings[0]);
      }
    }
  }, [coverageAreas, providerServicePricings]);

  useEffect(() => {
    if (allTripLegsInReservation?.length > 0) {
      const thisTripLeg = allTripLegsInReservation.filter(
        (tripLegInReservation) =>
          tripLegInReservation.tripLegReferenceID ===
          tripLeg.tripLegReferenceID,
      );
      const tripLegPayload =
        triplegUtils.getTripLegF1PayloadFromTransportResponse(tripLeg);
      if (thisTripLeg[0]?.servicePlans) {
        tripLegPayload.servicePlans = thisTripLeg[0].servicePlans;
        dispatch(fetchProvidersMatchingTripLegAsync(tripLegPayload));
      }
    } else if (loadedAllTripLegsInReservation) {
      dispatch(uiSlice.setErrorMessage('snackbar.reservationErrorEscalated'));
      escalateTripLeg({ force: true });
    }
  }, [loadedAllTripLegsInReservation, tripLeg]);

  useEffect(() => {
    if (
      loadedProviderCallOutcomes &&
      providerCallOutcomes &&
      !canReadEscalation
    ) {
      if (providerCallOutcomes.itemCount + 1 > maximumProviderAttempts) {
        escalateTripLeg();
      } else {
        setAttempt(providerCallOutcomes.itemCount + 1);
      }
    }
  }, [providerCallOutcomes]);

  return (
    <Root fullScreen open={open} onClose={handleClose}>
      <FullscreenDialogHeader
        title={title}
        subTitle={subTitle}
        close={handleClose}>
        {!canReadEscalation && (
          <DialogAttempts
            currentAttempt={attempt}
            maxAttempts={maximumProviderAttempts}
          />
        )}
      </FullscreenDialogHeader>
      {isLoading && <LinearProgress />}
      <DialogContent className={classes.content}>
        <Box className={classes.pages}>
          {canReadEscalation && !selectedProvider && (
            <ProviderSelectForm
              tripLeg={tripLeg}
              tripLegsInReservation={allTripLegsInReservation}
              selectedProvider={selectedProvider}
              setSelectedProvider={setSelectedProvider}
              close={handleClose}
            />
          )}
          {(!canReadEscalation || selectedProvider) && (
            <ProviderCallForm
              formHook={formHook}
              isSaving={savingProviderCallOutcome}
              isLoading={isLoading}
              isAllocationsValid={isAllocationsValid}
              tripLeg={tripLeg}
              tripLegsInReservation={allTripLegsInReservation}
              provider={selectedProvider}
              submit={formHook.handleSubmit(handleSubmit)}
              close={handleClose}
              currentAttempt={attempt}
              maxAttempts={maximumProviderAttempts}
            />
          )}
        </Box>
      </DialogContent>
      {isLoading && <LinearProgress />}
    </Root>
  );
}

export { FindProviderDialog };
